import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureToggle } from 'platform/utilities/feature-toggles';
import { focusElement } from 'platform/utilities/ui';

import useChatAnnouncements from '../../hooks/useChatAnnouncements';
import useChatScroll from '../../hooks/useChatScroll';
import useComposerFocus from '../../hooks/useComposerFocus';
import useDeferredPostError from '../../hooks/useDeferredPostError';
import useDirectLineConnection from '../../hooks/useDirectLineConnection';
import useSignInModal from '../../hooks/useSignInModal';
import useTranscriptNavigation from '../../hooks/useTranscriptNavigation';
import {
  chatbotActions,
  DEFAULT_CHATBOT_ERROR_MESSAGE,
  REQUEST_STATUS,
  selectChatbotError,
  selectChatbotMessages,
  selectChatbotRequestStatus,
} from '../../store';
import WebChatFrameworkBase from '../../transport/webChatFrameworkBase';
import { OPTION_TYPES } from '../../utils/directLineActivity';
import {
  COMPLETE,
  ERROR as TOKEN_ERROR,
  LOADING as TOKEN_LOADING,
} from '../../utils/loadingStatus';
import ChatComposer from './ChatComposer';
import ChatConversationComposerBridge from './ChatConversationComposerBridge';
import ChatMessageRegion, {
  INITIAL_LOADING_LABEL,
  INITIAL_LOADING_MESSAGE,
} from './ChatMessageRegion';
import ChatScreenReaderAnnouncer from './ChatScreenReaderAnnouncer';

/**
 * Stateful chat conversation experience with message list, typing indicator, and composer.
 * Welcome content is sourced from backend bot activities, not hardcoded UI copy,
 * so transcript content stays consistent with whichever bot backend is active.
 * @component
 * @returns {JSX.Element}
 */
export default function ChatConversation() {
  // External state
  const dispatch = useDispatch();
  const messages = useSelector(selectChatbotMessages);
  const requestStatus = useSelector(selectChatbotRequestStatus);
  const errorMessage = useSelector(selectChatbotError);
  const { useToggleValue, TOGGLE_NAMES } = useFeatureToggle();
  const showAiDisclaimer = Boolean(
    useToggleValue(TOGGLE_NAMES.virtualAgentShowAiDisclaimer),
  );
  const isTyping = requestStatus === REQUEST_STATUS.LOADING;

  // Hooks (each owns one concern; cross-concern wiring stays in the
  // handlers below)
  const { directLine, expired, tokenLoadingStatus, webChatStore } =
    useDirectLineConnection();
  const {
    assertiveAnnouncement,
    clearPendingBotAnnouncement,
    clearPendingTypingAnnouncement,
    politeAnnouncements,
    resetTypingAnnounced,
    scheduleTypingAnnouncement,
  } = useChatAnnouncements({ errorMessage, messages, showAiDisclaimer });
  const { focusComposerInput, handleComposerInputReady } = useComposerFocus();
  const {
    activeMessageIndex,
    handleExitMessageNavigation,
    handleMessageNavMove,
    handleTranscriptNavigationRequest,
    setActiveMessageIndex,
  } = useTranscriptNavigation({
    focusComposerInput,
    messageCount: messages.length,
  });
  const { messageEndRef, messageRegionRef } = useChatScroll({
    errorMessage,
    isTyping,
    messages,
  });
  const { clearPendingPostError, scheduleDeferredPostError } =
    useDeferredPostError({ focusComposerInput, resetTypingAnnounced });
  const showSignInModal = useSignInModal();

  // Derived values
  const isInitialLoading =
    tokenLoadingStatus === TOKEN_LOADING && messages.length === 0;
  const composerDisabled =
    tokenLoadingStatus !== COMPLETE || expired || !directLine;
  const [isStartingNewChat, setIsStartingNewChat] = React.useState(false);
  // Written by handleBotMessage, consumed by the options-focus effect.
  const pendingOptionsFocusMessageIdRef = React.useRef(null);

  // Handlers, in conversation-flow order: send -> typing -> bot message ->
  // feedback -> errors
  const handleSubmit = React.useCallback(
    (trimmedText, optionType) => {
      if (expired) {
        return;
      }

      setActiveMessageIndex(-1);
      clearPendingTypingAnnouncement();
      resetTypingAnnounced();
      clearPendingPostError();
      dispatch(chatbotActions.clearChatError());
      if (optionType !== OPTION_TYPES.ICON) {
        dispatch(chatbotActions.addUserMessage(trimmedText));
      }
      dispatch(chatbotActions.setRequestStatus(REQUEST_STATUS.LOADING));
      focusComposerInput();
    },
    [
      clearPendingPostError,
      clearPendingTypingAnnouncement,
      dispatch,
      expired,
      focusComposerInput,
      resetTypingAnnounced,
      setActiveMessageIndex,
    ],
  );

  // Restored history only: it adds the user's earlier message without the
  // loading status that a live send sets, since no reply is pending.
  const handleRestoredUserMessage = React.useCallback(
    mappedMessage => {
      dispatch(chatbotActions.addRestoredUserMessage(mappedMessage));
    },
    [dispatch],
  );

  const handleTyping = React.useCallback(() => {
    if (expired) {
      return;
    }

    scheduleTypingAnnouncement();
    clearPendingPostError();
    dispatch(chatbotActions.clearChatError());
    dispatch(chatbotActions.setRequestStatus(REQUEST_STATUS.LOADING));
  }, [clearPendingPostError, dispatch, expired, scheduleTypingAnnouncement]);

  const handleBotMessage = React.useCallback(
    mappedMessage => {
      if (expired) {
        return;
      }

      clearPendingTypingAnnouncement();
      resetTypingAnnounced();
      clearPendingPostError();
      dispatch(chatbotActions.clearChatError());
      dispatch(chatbotActions.addAssistantMessage(mappedMessage));

      if (mappedMessage.feedbackAcknowledgementTargetMessageId) {
        dispatch(
          chatbotActions.markFeedbackSubmitted(
            mappedMessage.feedbackAcknowledgementTargetMessageId,
          ),
        );
        focusComposerInput();
      }
      dispatch(chatbotActions.setRequestStatus(REQUEST_STATUS.SUCCEEDED));
      if (
        !mappedMessage.isFeedback &&
        Array.isArray(mappedMessage.options) &&
        mappedMessage.options.length > 0
      ) {
        pendingOptionsFocusMessageIdRef.current = mappedMessage.id;
      }
      // While navigating the transcript, follow new bot content, but land on
      // substantive answers, not the trailing feedback prompt.
      if (activeMessageIndex >= 0 && !mappedMessage.isFeedback) {
        setActiveMessageIndex(messages.length);
      }
    },
    [
      activeMessageIndex,
      clearPendingPostError,
      clearPendingTypingAnnouncement,
      dispatch,
      expired,
      focusComposerInput,
      messages.length,
      resetTypingAnnounced,
      setActiveMessageIndex,
    ],
  );

  const handleFeedbackPending = React.useCallback(
    promptMessageId => {
      dispatch(chatbotActions.setFeedbackPending(promptMessageId));
      focusComposerInput();
    },
    [dispatch, focusComposerInput],
  );

  const handleDeferredPostError = React.useCallback(
    pendingFeedbackMessageId => {
      if (expired) {
        return;
      }

      clearPendingTypingAnnouncement();
      scheduleDeferredPostError(pendingFeedbackMessageId);
    },
    [clearPendingTypingAnnouncement, expired, scheduleDeferredPostError],
  );

  const handleOpenSignInModal = React.useCallback(() => {
    showSignInModal();
  }, [showSignInModal]);

  const handleError = React.useCallback(() => {
    clearPendingPostError();
    clearPendingBotAnnouncement();
    clearPendingTypingAnnouncement();
    resetTypingAnnounced();
    dispatch(chatbotActions.setRequestStatus(REQUEST_STATUS.FAILED));
    dispatch(chatbotActions.setChatError(DEFAULT_CHATBOT_ERROR_MESSAGE));
    focusComposerInput();
  }, [
    clearPendingBotAnnouncement,
    clearPendingPostError,
    clearPendingTypingAnnouncement,
    dispatch,
    focusComposerInput,
    resetTypingAnnounced,
  ]);

  const handleStartNewChat = React.useCallback(() => {
    clearPendingPostError();
    clearPendingTypingAnnouncement();
    setIsStartingNewChat(true);
    dispatch(chatbotActions.resetConversation());
    window.dispatchEvent(new Event('va-chatbot-reset'));
  }, [clearPendingPostError, clearPendingTypingAnnouncement, dispatch]);

  // Focus the first option of a newly rendered option prompt.
  React.useEffect(() => {
    const pendingId = pendingOptionsFocusMessageIdRef.current;
    const optionControl = document
      .getElementById(`chat-message-content-${pendingId}`)
      ?.closest('li')
      ?.querySelector('[data-testid="chat-message-option"]');
    if (!optionControl) {
      return;
    }
    pendingOptionsFocusMessageIdRef.current = null;
    focusElement('button', {}, optionControl);
  }, [messages]);

  // Announcement side effects for new bot messages and errors live in
  // useChatAnnouncements; only the focus move stays here.
  React.useEffect(() => {
    if (errorMessage) {
      focusComposerInput();
    }
  }, [errorMessage, focusComposerInput]);

  React.useEffect(() => {
    if (
      isStartingNewChat &&
      (tokenLoadingStatus === TOKEN_ERROR ||
        (tokenLoadingStatus === COMPLETE && !expired))
    ) {
      setIsStartingNewChat(false);
    }
  }, [expired, isStartingNewChat, tokenLoadingStatus]);

  React.useEffect(() => {
    if (tokenLoadingStatus === TOKEN_LOADING && messages.length === 0) {
      dispatch(chatbotActions.setRequestStatus(REQUEST_STATUS.LOADING));
    }

    if (
      !isStartingNewChat &&
      (tokenLoadingStatus === TOKEN_ERROR ||
        (expired && tokenLoadingStatus === COMPLETE))
    ) {
      handleError();
    }
  }, [
    dispatch,
    expired,
    handleError,
    isStartingNewChat,
    messages.length,
    tokenLoadingStatus,
  ]);

  return (
    <div className="va-chatbot-chat-thread" data-testid="chat-conversation">
      {directLine ? (
        <WebChatFrameworkBase.Components.Composer
          directLine={directLine}
          store={webChatStore}
        >
          <ChatConversationComposerBridge
            activeMessageIndex={activeMessageIndex}
            disabled={composerDisabled}
            errorMessage={errorMessage}
            isTyping={isTyping && !isInitialLoading}
            messageEndRef={messageEndRef}
            messageRegionRef={messageRegionRef}
            messages={messages}
            onActiveMessageIndexChange={handleMessageNavMove}
            onBotMessage={handleBotMessage}
            onComposerInputReady={handleComposerInputReady}
            onExitMessageNavigation={handleExitMessageNavigation}
            onFeedbackPending={handleFeedbackPending}
            onOpenSignInModal={handleOpenSignInModal}
            onPostError={handleDeferredPostError}
            onRestoredUserMessage={handleRestoredUserMessage}
            onSendMessage={handleSubmit}
            onStartNewChat={
              tokenLoadingStatus === TOKEN_ERROR || expired
                ? handleStartNewChat
                : undefined
            }
            onTranscriptNavigationRequest={handleTranscriptNavigationRequest}
            onTyping={handleTyping}
            showAiDisclaimer={showAiDisclaimer}
          />
        </WebChatFrameworkBase.Components.Composer>
      ) : (
        <>
          <ChatMessageRegion
            activeMessageIndex={activeMessageIndex}
            errorMessage={errorMessage}
            isInitialLoading={isInitialLoading}
            isTyping={isTyping}
            messageEndRef={messageEndRef}
            messageRegionRef={messageRegionRef}
            messages={messages}
            onActiveMessageIndexChange={handleMessageNavMove}
            onExitMessageNavigation={handleExitMessageNavigation}
            onOptionSelect={handleSubmit}
            onStartNewChat={
              tokenLoadingStatus === TOKEN_ERROR || expired
                ? handleStartNewChat
                : undefined
            }
            showAiDisclaimer={showAiDisclaimer}
          />
          <ChatComposer
            disabled
            onComposerInputReady={handleComposerInputReady}
            onTranscriptNavigationRequest={handleTranscriptNavigationRequest}
          />
        </>
      )}
      {directLine && isInitialLoading ? (
        <div className="vads-u-padding-x--1p5 vads-u-padding-bottom--1">
          <va-loading-indicator
            data-testid="chat-initial-loading-indicator"
            label={INITIAL_LOADING_LABEL}
            message={INITIAL_LOADING_MESSAGE}
          />
        </div>
      ) : null}
      <ChatScreenReaderAnnouncer
        assertiveAnnouncement={assertiveAnnouncement}
        politeAnnouncements={politeAnnouncements}
      />
    </div>
  );
}
